import { useState } from "react";
import {
  Clock, MapPin, CheckCircle2, XCircle, ShieldCheck, AlertTriangle,
  Sparkles, Loader2, ListChecks, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const SEV_CLS = {
  low: "bg-[#E6F0EB] border-[#34745C]/20 text-[#34745C]",
  medium: "bg-[#FDF3E1] border-[#C48231]/20 text-[#C48231]",
  high: "bg-[#FCEAE8] border-[#B54D40]/20 text-[#B54D40]",
};

const ALIGN_BADGE = {
  aligned: "bg-[#E6F0EB] text-[#34745C]",
  partial: "bg-[#FDF3E1] text-[#C48231]",
  misaligned: "bg-[#FCEAE8] text-[#B54D40]",
};

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");
const gpsStr = (g) => (g ? `${g.lat.toFixed(4)}, ${g.lng.toFixed(4)}` : "Not captured");

const SectionTitle = ({ icon: Icon, children }) => (
  <h3 className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#57605A]">
    <Icon className="w-4 h-4" strokeWidth={1.5} /> {children}
  </h3>
);

export const VisitDetailDialog = ({ schedule: s, onClose, canManage, onUpdated }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [reverifying, setReverifying] = useState(false);

  if (!s) return null;
  const a = s.alignment_report;
  const required = s.required_tasks || [];
  const done = s.tasks_completed || [];
  const extras = done.filter((t) => !required.includes(t));

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const { data } = await api.post(`/schedules/${s.id}/analyze-alignment`);
      onUpdated(data);
      toast.success("Care plan alignment analyzed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Analysis failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const reverify = async () => {
    setReverifying(true);
    try {
      const { data } = await api.post(`/schedules/${s.id}/verify-evv`);
      onUpdated(data);
      toast.success(`EVV re-verified: ${data.evv_status}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Verification failed");
    } finally {
      setReverifying(false);
    }
  };

  return (
    <Dialog open={!!s} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="visit-detail-dialog" className="bg-[#F9F7F4] border-[#E8E6E1] sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {s.client_name} <span className="text-[#57605A] font-normal">with</span> {s.caregiver_name}
          </DialogTitle>
          <p className="text-sm text-[#57605A]">
            {s.visit_type} · {s.date} · {s.start_time}–{s.end_time} · <span className="uppercase font-semibold">{s.status.replace("_", " ")}</span>
          </p>
        </DialogHeader>

        <div className="space-y-5 mt-1">
          <div className="bg-white border border-[#E8E6E1] rounded-lg p-4 grid sm:grid-cols-2 gap-3">
            <div>
              <SectionTitle icon={Clock}>Clock in / out</SectionTitle>
              <p className="mt-2 text-sm" data-testid="evv-times">
                In: <span className="font-medium">{fmtDT(s.actual_start)}</span>
                <br />
                Out: <span className="font-medium">{fmtDT(s.actual_end)}</span>
              </p>
            </div>
            <div>
              <SectionTitle icon={MapPin}>GPS location</SectionTitle>
              <p className="mt-2 text-sm" data-testid="evv-gps">
                In: <span className={s.gps_in ? "font-medium" : "text-[#B54D40]"}>{gpsStr(s.gps_in)}</span>
                <br />
                Out: <span className={s.gps_out ? "font-medium" : "text-[#B54D40]"}>{gpsStr(s.gps_out)}</span>
              </p>
            </div>
          </div>

          {(required.length > 0 || done.length > 0) && (
            <div className="bg-white border border-[#E8E6E1] rounded-lg p-4">
              <SectionTitle icon={ListChecks}>Tasks</SectionTitle>
              <ul className="mt-3 space-y-1.5" data-testid="visit-tasks">
                {required.map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm">
                    {done.includes(t) ? (
                      <CheckCircle2 className="w-4 h-4 text-[#34745C]" strokeWidth={1.5} />
                    ) : (
                      <XCircle className="w-4 h-4 text-[#B54D40]" strokeWidth={1.5} />
                    )}
                    <span className={done.includes(t) ? "" : "text-[#B54D40]"}>{t}</span>
                  </li>
                ))}
                {extras.map((t) => (
                  <li key={t} className="flex items-center gap-2 text-sm text-[#57605A]">
                    <CheckCircle2 className="w-4 h-4 text-[#34745C]" strokeWidth={1.5} /> {t} <span className="text-xs">(additional)</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {s.visit_notes && (
            <div className="bg-white border border-[#E8E6E1] rounded-lg p-4">
              <SectionTitle icon={StickyNote}>Visit notes</SectionTitle>
              <p className="mt-2 text-sm italic text-[#1C211F]">"{s.visit_notes}"</p>
            </div>
          )}

          {s.status === "completed" && (
            <div className="bg-white border border-[#E8E6E1] rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <SectionTitle icon={ShieldCheck}>EVV verification</SectionTitle>
                <div className="flex items-center gap-2">
                  {s.evv_status && (
                    <span
                      data-testid="evv-status-badge"
                      className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${
                        s.evv_status === "verified" ? "bg-[#E6F0EB] text-[#34745C]" : "bg-[#FCEAE8] text-[#B54D40]"
                      }`}
                    >
                      {s.evv_status}
                    </span>
                  )}
                  {canManage && (
                    <button
                      data-testid="reverify-evv-btn"
                      onClick={reverify}
                      disabled={reverifying}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#2E5C50] hover:text-[#22463D] transition-colors duration-150 disabled:opacity-50"
                    >
                      {reverifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} /> : <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />}
                      Re-verify
                    </button>
                  )}
                </div>
              </div>
              {(s.evv_issues || []).length === 0 ? (
                <p className="mt-3 text-sm text-[#34745C] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} /> All EVV checks passed — clock times, location and tasks verified.
                </p>
              ) : (
                <div className="mt-3 space-y-2" data-testid="evv-issues">
                  {s.evv_issues.map((iss, i) => (
                    <div key={i} className={`border rounded-lg p-3 ${SEV_CLS[iss.severity] || SEV_CLS.medium}`}>
                      <p className="text-xs font-bold uppercase tracking-wider">{iss.severity}</p>
                      <p className="text-sm text-[#1C211F] mt-0.5">{iss.message}</p>
                      <p className="text-xs text-[#57605A] mt-1">→ {iss.correction}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {s.status === "completed" && (
            <div className="bg-white border border-[#E8E6E1] rounded-lg p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <SectionTitle icon={Sparkles}>Care plan alignment</SectionTitle>
                {a && (
                  <span data-testid="alignment-status-badge" className={`px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider ${ALIGN_BADGE[a.alignment_status] || ALIGN_BADGE.partial}`}>
                    {a.alignment_status}
                  </span>
                )}
              </div>
              {!a ? (
                <div className="mt-3">
                  <p className="text-sm text-[#57605A]">
                    {canManage
                      ? "Run Nexari's AI to compare this visit against the client's care plan and physician orders."
                      : "The agency has not run a care plan alignment check for this visit yet."}
                  </p>
                  {canManage && (
                    <button
                      data-testid="analyze-alignment-btn"
                      onClick={analyze}
                      disabled={analyzing}
                      className="mt-3 flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors duration-200 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-60"
                    >
                      {analyzing ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : <Sparkles className="w-4 h-4" strokeWidth={1.5} />}
                      {analyzing ? "Analyzing — about 20–30 seconds…" : "Analyze alignment"}
                    </button>
                  )}
                </div>
              ) : (
                <div className="mt-3 space-y-3" data-testid="alignment-report">
                  {s.alignment_at && (
                    <p className="text-xs text-[#57605A]">Last analyzed {new Date(s.alignment_at).toLocaleString()}</p>
                  )}
                  <p className="text-sm leading-relaxed text-[#1C211F]">{a.summary}</p>
                  {(a.mismatches || []).map((m, i) => (
                    <div key={i} className={`flex items-start gap-2.5 border rounded-lg p-3 ${SEV_CLS[m.severity] || SEV_CLS.medium}`}>
                      <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
                      <p className="text-sm text-[#1C211F]">{m.message}</p>
                    </div>
                  ))}
                  {(a.corrective_actions || []).length > 0 && (
                    <ol className="space-y-1.5">
                      {a.corrective_actions.map((c, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed">
                          <span className="w-5 h-5 bg-[#2E5C50] text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{i + 1}</span>
                          {c}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
